import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID || '1489711356889534554';

export function useDiscordAuth() {
  const [discordUser, setDiscordUser] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [hasCustomerRole, setHasCustomerRole] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const fetchDiscordUser = useCallback(async (token, userData = null) => {
    if (!userData) setIsConnecting(true);
    try {
      let data = userData;
      if (!data) {
        // 1. Get basic user info
        const response = await fetch('https://discord.com/api/users/@me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.status === 401) {
          console.warn("Discord Session Expired (401)");
          localStorage.removeItem('discord_access_token');
          localStorage.removeItem('discord_user');
          setDiscordUser(null);
          return;
        }

        if (response.status === 429) {
          console.warn("Discord Rate Limit Hit");
          // Try to recover from cache if available
          const cached = localStorage.getItem('discord_user');
          if (cached) {
            data = JSON.parse(cached);
            setDiscordUser(data);
          } else {
            return;
          }
        } else if (!response.ok) {
          throw new Error(`Discord API Error: ${response.status}`);
        } else {
          data = await response.json();
          if (data.id) {
            setDiscordUser(data);
            localStorage.setItem('discord_user', JSON.stringify(data));
          }
        }
      } else {
        // We already have userData (passed from cache)
        setDiscordUser(data);
      }
      
      if (data && data.id) {
        // 2. Call our secure backend to sync profile and check roles
        const cacheKey = `discord_roles_${data.id}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const cachedRoles = JSON.parse(cached);
          if (Date.now() - cachedRoles.timestamp < 300000) { // 5 minutes
            setIsMember(cachedRoles.isMember);
            setHasCustomerRole(cachedRoles.isCustomer);
            setIsAdmin(cachedRoles.isAdmin);
            setIsConnecting(false);
            return;
          }
        }

        try {
          const syncRes = await fetch('/.netlify/functions/get-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken: token, type: 'sync_user' }),
          });
          
          if (syncRes.ok) {
            const result = await syncRes.json();
            setIsMember(result.isMember);
            setHasCustomerRole(result.isCustomer);
            setIsAdmin(result.isAdmin);
            
            // Store in cache
            localStorage.setItem(cacheKey, JSON.stringify({
              isMember: result.isMember,
              isCustomer: result.isCustomer,
              isAdmin: result.isAdmin,
              timestamp: Date.now()
            }));
          } else if (syncRes.status === 401) {
            console.warn("Backend Auth Error - Potential Invalid Token");
          }
        } catch (sbErr) {
          console.error("Backend Sync Error:", sbErr);
        }
      }
    } catch (err) {
      console.error("Fetch Discord User Error:", err);
      // Don't clear tokens on general network errors to avoid logging users out prematurely
    } finally {
      setIsConnecting(false);
    }
  }, []);

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    let accessToken = fragment.get('access_token');

    if (accessToken) {
      localStorage.setItem('discord_access_token', accessToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchDiscordUser(accessToken);
    } else {
      const savedToken = localStorage.getItem('discord_access_token');
      const cachedUser = localStorage.getItem('discord_user');
      if (savedToken) {
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            fetchDiscordUser(savedToken, userData);
          } catch (e) {
            fetchDiscordUser(savedToken);
          }
        } else {
          fetchDiscordUser(savedToken);
        }
      }
    }
  }, [fetchDiscordUser]);

  const handleConnectDiscord = () => {
    const redirectUri = window.location.origin;
    const scope = encodeURIComponent('identify guilds.members.read guilds.join');
    const url = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${scope}`;
    window.location.href = url;
  };

  const handleLogout = () => {
    setDiscordUser(null);
    setIsMember(false);
    setHasCustomerRole(false);
    setIsAdmin(false);
    localStorage.removeItem('discord_access_token');
    localStorage.removeItem('discord_user');
    toast.info('Logged out from Discord');
  };

  return {
    discordUser,
    isMember,
    hasCustomerRole,
    isAdmin,
    isConnecting,
    handleConnectDiscord,
    handleLogout
  };
}
